import { create } from 'zustand';
import { pushSettingFields } from './settingsPending';

// Port of ThemeModeNotifier from lib/providers/settings_providers.dart.
// Synced to user_settings.theme_mode (migration 181, owner decision
// 2026-10-10): an explicit light/dark follows the account; 'system' still
// follows each device's OS. Cached in localStorage for an instant boot, and
// device-local on a server without 181 (the patch skips the column).

export type ThemeMode = 'light' | 'dark' | 'system';

const KEY = 'settings.theme_mode';

function load(): ThemeMode {
  const raw = localStorage.getItem(KEY);
  return raw === 'light' || raw === 'dark' ? raw : 'system';
}

/** Resolves the effective brightness, consulting the OS for 'system'. */
export function resolveBrightness(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return mode;
}

/** Applies the resolved brightness by toggling the `.dark` class on <html>. */
export function applyBrightness(mode: ThemeMode): void {
  document.documentElement.classList.toggle('dark', resolveBrightness(mode) === 'dark');
}

interface ThemeState {
  mode: ThemeMode;
  /** The user picked a mode — persists, applies and patches theme_mode. */
  setMode: (mode: ThemeMode) => void;
  /** From settings sync / a reset — persists and applies WITHOUT re-pushing. */
  applyRemote: (mode: ThemeMode) => void;
}

export const useThemeStore = create<ThemeState>((set) => ({
  mode: load(),
  setMode: (mode) => {
    set({ mode });
    localStorage.setItem(KEY, mode);
    applyBrightness(mode);
    void pushSettingFields({ theme_mode: mode });
  },
  applyRemote: (mode) => {
    set({ mode });
    localStorage.setItem(KEY, mode);
    applyBrightness(mode);
  },
}));

// Follow the OS theme LIVE while on 'system' — not just at boot — so the app's
// appearance stays consistent with the device the moment it changes.
if (typeof window !== 'undefined' && window.matchMedia) {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (useThemeStore.getState().mode === 'system') applyBrightness('system');
  });
}
